// hooks
import useSelectedDateRange from 'components/roster-portal/activity-planning/hooks/useSelectedDateRange';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityStore } from './stores/entityStore/entityStore';

// types
import type {
  EventResponse,
  PlanningEntityIdentifier,
  PlanningEntityResponse
} from 'generated/index';
import type { AppointmentRecurrenceType } from './components/AppointmentForm/AppointmentForm.types';
import type { UseEntityListenerProps, UseMrpPlanning } from './mrp.types';

// utils
import { showErrorSnackbar } from 'components/shared/Snackbar';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import { requests } from 'RestAPIs/rapini';
import { useShallow } from 'zustand/shallow';
import { showSnackbar } from '../shared';
import {
  createAvailabilityEventsFromPlanningEntityResponse,
  createCalendarEventsFromPlanningEntityResponse
} from './components/Calendar/Calendar.utils';
import { getMatchingEntityIdentifiers } from './mrp.utils';

export const useMrpPlanning = ({
  planningEntityIdentifiers,
  startDate,
  endDate,
  enabled = true,
  dependencies = []
}: UseMrpPlanning) => {
  'use memo';
  const { t } = useTranslation('common');

  /**
   * @description
   * We use the planning identifiers to create unique query keys which
   * we can use to invalidate components at the entity level
   */
  const planningEntityIdentifierQueryKeys: string[] = planningEntityIdentifiers
    .map(({ entityId }) => entityId)
    .filter((entityId) => entityId !== undefined);

  return useQuery(
    [
      'mrpPlanning',
      startDate,
      endDate,
      ...planningEntityIdentifierQueryKeys,
      ...dependencies
    ],
    async () => {
      if (!planningEntityIdentifiers || planningEntityIdentifiers.length == 0) {
        return {
          planningEntities: []
        };
      }

      return await requests.getPlanning({
        planningEntityIdentifiers,
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD')
      });
    },
    {
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: 0,
      refetchIntervalInBackground: false,
      retry: 0,
      retryDelay: 0,
      cacheTime: 0,
      staleTime: 0,
      enabled
    }
  );
};

export const deleteMrpAppointment = async (
  event: EventResponse,
  editAppointmentType?: AppointmentRecurrenceType
) => {
  const { id, start } = event ?? {};
  if (!id) return;

  return await requests
    .deleteAppointment({
      appointmentOccurrenceIdentifier: {
        entityId: id,
        originalOccurrenceStart: new Date(start).toISOString()
      },
      editAppointmentType: editAppointmentType || 'ONLY_THIS'
    })
    .catch(() => {
      showErrorSnackbar();
    });
};

export const useEntityListener = ({
  calendarId = '',
  planningEntityIdentifiers,
  listen = true
}: UseEntityListenerProps) => {
  'use memo';
  const {
    data,
    subscribers,
    control: { subscribe, fetch, unsubscribe, isSubscribed, setAnyLoading }
  } = useEntityStore(
    useShallow((state) => ({
      data: state.data,
      subscribers: state.subscribers,
      control: state.control
    }))
  );
  /**
   * @description If any of the planning entity identifiers return isLoading or an isError state,
   * we set those values to true.
   */
  const peiQueryStates = getMatchingEntityIdentifiers(
    subscribers,
    planningEntityIdentifiers
  );
  const isLoading = peiQueryStates.some((state) => state.isLoading);
  const isError = peiQueryStates.some((state) => state.isError);

  const { startDate, endDate } = useSelectedDateRange();

  /**
   * @description Check if the current planning entity identifiers are subscribed to in the entity store.
   */
  const isSubscribedToPlanning =
    getMatchingEntityIdentifiers(planningEntityIdentifiers, subscribers)
      .length > 0;

  /**
   * @description Check if the incoming data contains planning entity identifiers that match the subscribed planning entity identifiers.
   */
  const isSubscribedToIncomingData =
    isSubscribedToPlanning &&
    data.some(
      ({ planningEntityIdentifier }) =>
        getMatchingEntityIdentifiers(
          [planningEntityIdentifier],
          planningEntityIdentifiers
        ).length > 0
    );

  /**
   * @description A persisted reference to the data rendered by the current entity, regardless of the entity store's current data state.
   */
  const [entityData, setEntityData] = useState<PlanningEntityResponse[]>([]);

  const fetchDataForEntity = () =>
    fetch({
      planningEntityIdentifiers,
      startDate,
      endDate
    });

  const refetchByPlanningEntityIdentifiers = (
    planningEntityIdentifiers: PlanningEntityIdentifier[]
  ) =>
    fetch({
      planningEntityIdentifiers,
      startDate,
      endDate
    });

  /**
   * @description If the entity store's data contains matching planning entity identifiers,
   * create calendar events & availabilities from the data.
   */
  // If the entity store is not subscribed to the planning entity identifiers, fall back to the persisted data.
  const subscribedData =
    listen && isSubscribedToIncomingData
      ? data.filter(
          ({ planningEntityIdentifier }) =>
            getMatchingEntityIdentifiers(
              [planningEntityIdentifier],
              planningEntityIdentifiers
            ).length > 0
        )
      : entityData;

  const events = createCalendarEventsFromPlanningEntityResponse(
    calendarId,
    subscribedData
  );
  const availabilities = createAvailabilityEventsFromPlanningEntityResponse(
    calendarId,
    subscribedData
  );

  /**
   * @description The resolved planning entities for the current identifiers, always read directly
   * from the store. Unlike events/availabilities this is not gated on `listen` so callers with
   * listen: false (e.g. CalendarHeader) still receive the data once the store is populated.
   */
  const planningEntities = data.filter(
    ({ planningEntityIdentifier }) =>
      getMatchingEntityIdentifiers(
        [planningEntityIdentifier],
        planningEntityIdentifiers
      ).length > 0
  );

  useEffect(() => {
    if (isSubscribedToPlanning && isSubscribedToIncomingData) {
      setEntityData(
        data.filter(
          ({ planningEntityIdentifier }) =>
            getMatchingEntityIdentifiers(
              [planningEntityIdentifier],
              planningEntityIdentifiers
            ).length > 0
        )
      );
    }
  }, [
    data,
    isSubscribedToIncomingData,
    isSubscribedToPlanning,
    planningEntityIdentifiers
  ]);

  useEffect(() => {
    if (!listen) return;

    /**
     * @description Subscribe to planning data if not already subscribed.
     */
    if (!isSubscribed(planningEntityIdentifiers))
      return void subscribe(planningEntityIdentifiers);

    /**
     * @description A one-time request to fetch data for the current entity identifiers
     */

    if (entityData.length === 0) {
      fetch({ planningEntityIdentifiers, startDate, endDate });
    }

    /**
     * @description Unsubscribe from planning data when the hook unmounts
     */
    return () => {
      unsubscribe(planningEntityIdentifiers);
    };
  }, [
    fetch,
    startDate,
    endDate,
    isSubscribed,
    isSubscribedToPlanning,
    listen,
    planningEntityIdentifiers,
    subscribe,
    unsubscribe,
    entityData.length
  ]);

  useEffect(() => {
    setAnyLoading(subscribers.some(({ isLoading }) => isLoading));
  }, [setAnyLoading, subscribers]);

  return {
    events,
    availabilities,
    planningEntities,
    isLoading,
    isError,
    /**
     * @description Refetch the data for the current entity identifier(s)
     */
    refetch: fetchDataForEntity,
    /**
     * @description Refetch data for the selected planning entity identifier(s).
     */
    refetchByPlanningEntityIdentifiers
  };
};
